'use client';

import classnames from 'classnames';
import Image, { StaticImageData } from 'next/image';
import React, { HTMLAttributeAnchorTarget } from 'react';

import Button from '@/components/button/Button';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type BannerProps = {
    className?: string;
    classNameImage?: string;
    classNameLeadingImage?: string;
    classNameTrailingImage?: string;
    backgroundImage: string | StaticImageData;
    leadingImage?: string | StaticImageData;
    trailingImage?: string | StaticImageData;
    text?: string;
    href: string;
    target?: HTMLAttributeAnchorTarget | undefined;
    buttonContainerLeadingComponent?: React.JSX.Element;
    buttonLeadingIcon?: React.JSX.Element;
    buttonTrailingIcon?: React.JSX.Element;
    buttonTitle: string;
    withOverlay?: boolean;
};

function Banner(props: BannerProps): React.JSX.Element {
    const {
        className,
        classNameImage,
        classNameLeadingImage,
        classNameTrailingImage,
        backgroundImage,
        leadingImage,
        trailingImage,
        text,
        href,
        target,
        buttonContainerLeadingComponent,
        buttonLeadingIcon,
        buttonTrailingIcon,
        buttonTitle,
        withOverlay
    } = props;

    const { track } = useAnalytics();
    const domainConfiguration = useDomainConfiguration();

    return (
        <div className={classnames(className, 'relative w-full h-120 rounded-8 overflow-clip bg-darkBlue-bannerBg')}>
            <Image
                className={classnames(classNameImage, 'absolute top-0 left-0 h-full z-0 object-cover object-center')}
                src={backgroundImage}
                width={2000}
                height={500}
                alt={buttonTitle}
            />
            {leadingImage && (
                <Image
                    className={classnames(
                        classNameLeadingImage,
                        'absolute top-0 left-0 w-auto h-full z-0 object-cover object-center'
                    )}
                    src={leadingImage}
                    width={2000}
                    height={500}
                    alt={buttonTitle}
                />
            )}
            {trailingImage && (
                <Image
                    className={classnames(
                        classNameTrailingImage,
                        'absolute top-0 right-0 w-auto h-full z-0 object-cover object-center'
                    )}
                    src={trailingImage}
                    width={2000}
                    height={500}
                    alt={buttonTitle}
                />
            )}
            {withOverlay !== false && <div className="absolute h-full w-full bg-darkBlue/25" />}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full">
                <div className="flex flex-col items-center space-y-16 w-full px-8">
                    {text && <span className="text-center text-body-bold-14 text-white">{text}</span>}
                    <div className="flex flex-row space-x-12 items-center">
                        {buttonContainerLeadingComponent}
                        <Link
                            href={href}
                            target={target}
                            onClick={() => track('Book Cinema Clicked', { source: 'rent_cinema_card' })}>
                            <Button
                                className="h-36"
                                css={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: domainConfiguration.style.onPrimaryColor
                                }}
                                leadingIcon={buttonLeadingIcon}
                                trailingIcon={buttonTrailingIcon}>
                                {buttonTitle}
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Banner;
